import { useState, useEffect } from 'react';
import { ExternalLink, ArrowUpRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { api } from '../../context/AuthContext';

export function OrganicLandingPagesTable({ projectId } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = { days: 30 };
    if (projectId) params.project_id = projectId;
    api.get('/analytics/landing-pages', { params })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Could not load landing pages'))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) {
    return (
      <Card className="gds-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-base">
            <ArrowUpRight className="w-4 h-4 text-violet-500" />
            Organic Landing Pages
          </CardTitle>
          <CardDescription>Top pages driving organic traffic · Last 30 days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data?.pages?.length) return null;

  const { pages, has_revenue } = data;

  return (
    <Card className="gds-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2 text-base">
          <ArrowUpRight className="w-4 h-4 text-violet-500" />
          Organic Landing Pages
        </CardTitle>
        <CardDescription>
          Top pages driving organic traffic · Last 30 days
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-800">
                <th className="text-left py-2 px-3 text-xs text-slate-500 font-medium">Page</th>
                <th className="text-right py-2 px-3 text-xs text-slate-500 font-medium">Sessions</th>
                <th className="text-right py-2 px-3 text-xs text-slate-500 font-medium">Conversions</th>
                {has_revenue && (
                  <th className="text-right py-2 px-3 text-xs text-slate-500 font-medium">Revenue</th>
                )}
                <th className="text-right py-2 px-3 text-xs text-slate-500 font-medium">Engagement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
              {pages.map((page, i) => (
                <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1 max-w-[280px]">
                      <span className="truncate text-slate-700 dark:text-slate-300 text-xs">
                        {page.page}
                      </span>
                      <ExternalLink className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right font-medium">
                    {page.sessions.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {page.conversions > 0 ? (
                      <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800">
                        {page.conversions}
                      </Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  {has_revenue && (
                    <td className="py-2 px-3 text-right text-emerald-600 font-medium text-xs">
                      {page.revenue > 0 ? `$${page.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                    </td>
                  )}
                  <td className="py-2 px-3 text-right text-xs text-slate-500">
                    {page.engagement_rate}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
